import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { normalizeNfc } from "../../utils/text";

// ── 型定義（BE: docs/api/notes.md に基づく） ──────────────────────────

/** セクション権限設定で選択できるプロジェクト内ポジション（API 表記） */
export type NotePosition =
  | "CLIENT"
  | "REVIEWER"
  | "PRODUCER"
  | "DIRECTOR"
  | "SPECIALIST";

/**
 * セクション内の実効ロール。
 * - viewer = ロール無しだが閲覧可（対象プロジェクトのメンバー）
 * - public = is_public セクションを閲覧する非メンバー（未ログインを含む）。
 *   閲覧のみでリアクション不可。作成者情報（created_by 等）は返らない
 */
export type NoteRole =
  | "public"
  | "viewer"
  | "contributor"
  | "maintainer"
  | "owner";

/** ロール設定で割り当てられるロール */
export type SectionRoleName = "owner" | "maintainer" | "contributor";

/** セクションの保護ルール */
export interface SectionProtection {
  /** プルリクのマージにオーナーの承認を必須にするか */
  require_owner_approval: boolean;
  /** マージに必要な承認人数（起票者以外） */
  required_approvals: number;
}

/** ロール割り当て 1 件（position / user はどちらか一方） */
export interface SectionRoleEntry {
  id: number;
  role: SectionRoleName;
  position: NotePosition | null;
  user: NoteUserBrief | null;
}

/** BE の access 情報（ツリー/詳細レスポンス共通） */
export interface NoteAccessFields {
  my_role: NoteRole;
  /** 直接の削除・フォルダ操作・並べ替えができるか（メンテナー以上） */
  can_edit: boolean;
  /** PR（下書き）を作れるか（コントリビューター以上） */
  can_contribute: boolean;
  /** PR を経ない直接更新（「更新」ボタン）ができるか */
  can_direct_update: boolean;
}

/**
 * 本文（Markdown）を持つページの表示形式。カード（共有TIPS）は独立モデルではなく
 * display_style=card のノート（同じ Markdown のまま表示だけ切り替えられる）。
 * エディタ・プルリクエスト（target_type）はこの 2 種類だけを扱う。
 */
export type NoteContentStyle = "note" | "card";

/**
 * ページの表示形式。board はイメージボード（中身は imageBoardApi で直接編集し、
 * ノート / カードとは相互に切り替えられない）。
 */
export type NoteDisplayStyle = NoteContentStyle | "board";

/** エディタ / プルリクエストで扱う表示形式へ寄せる（ボードはノート扱い） */
export const toContentStyle = (style: NoteDisplayStyle): NoteContentStyle =>
  style === "card" ? "card" : "note";

/** ツリーに含まれるノートのサマリー（本文なし） */
export interface NoteSummary {
  id: string;
  title: string;
  display_style: NoteDisplayStyle;
  /** カード表示時のカテゴリ（垂れ帯表示用） */
  category: string;
  /** ホームのやることリストに投稿するか（カード表示時のみ意味を持つ） */
  is_published: boolean;
  is_required: boolean;
  /** 所属フォルダ（null = セクション直下） */
  folder_id: string | null;
  sort_order: number;
  modified: string | null;
  /** 自分の未提出の更新提案（下書き）があるか（本人にのみ true） */
  has_my_draft: boolean;
}

/**
 * 自分の下書き（新規作成の変更提案）。ツリーに「下書き」行として表示し、
 * 開いて編集・プルリクエスト提出ができる（本人にのみ返る）
 */
export interface NoteDraftSummary {
  id: string;
  title: string;
  display_style: NoteDisplayStyle;
  /** 配置先フォルダ（null = セクション直下）。マージ時にここへ作成される */
  folder_id: string | null;
  /**
   * 配置コンテナ内の並び位置（フォルダ・ノート共通の sort_order 空間）。
   * この値以上の行の手前に表示し、マージ時もその位置へ挿入される。
   * null = コンテナの末尾
   */
  sort_order: number | null;
  modified: string | null;
}

/** セクション内フォルダ（ノート/カード共用・入れ子可） */
export interface NoteFolderSummary {
  id: string;
  title: string;
  /** 親フォルダ（null = セクション直下）。ライブラリと同方式の階層 */
  parent_id: string | null;
  sort_order: number;
  /** このフォルダ自身の「プルリク承認不要」設定 */
  skip_review: boolean;
  /**
   * 祖先の設定も含めた承認不要の実効値。true の中では、コントリビューター
   * 以上がプルリクエストなしでノート/カードを作成・更新できる
   */
  effective_skip_review: boolean;
  /** 有効な共有リンクが発行されているか（メンテナー以上にのみ true が返る） */
  is_shared: boolean;
}

/** フォルダの共有リンク（共有ライブラリと同じ仕様） */
export interface NoteShareLink {
  id: string;
  token: string;
  expires_at: string | null;
  require_membership: boolean;
}

export interface SaveNoteShareLinkParams {
  folder_id: string;
  /** 有効期限（ISO 日時 or YYYY-MM-DD。null/省略 = 無期限） */
  expires_at?: string | null;
  /** セクションの対象プロジェクトのメンバーのみに制限（既定 true） */
  require_membership?: boolean;
}

/** セクションの権限設定（master_admin / セクションオーナーに返る） */
export interface NoteSectionSettings {
  /** 非ログインユーザーにも閲覧を許可するか（外部公開。閲覧のみ） */
  is_public: boolean;
  /** マイページ「ヘルプセンター」への割当（クライアント向け） */
  help_center_client: boolean;
  /** マイページ「ヘルプセンター」への割当（クリエイター向け） */
  help_center_creator: boolean;
  projects: { id: string; name: string }[];
  allowed_positions: NotePosition[];
  /** false = プロジェクト進行中のみアクセス可 */
  allow_completed: boolean;
  /** ロール設定（オーナー / メンテナー / コントリビューター） */
  roles: SectionRoleEntry[];
  require_owner_approval: boolean;
  required_approvals: number;
}

export interface NoteSection extends NoteAccessFields {
  id: string;
  title: string;
  sort_order: number;
  /** ロール・保護ルールを変更できるか（master_admin またはオーナー） */
  can_manage_roles: boolean;
  /** 保護ルール（全閲覧者に返る） */
  protection: SectionProtection;
  /** 自分に見えるレビュー待ち（open）の変更提案数（下書きは含めない） */
  open_change_count: number;
  /** 対象プロジェクトが 1 件のときそのバナーカラー（見出し行の配色用）。それ以外は null */
  banner_color: string | null;
  /** ノート（display_style=card のカード表示ノートを含む） */
  notes: NoteSummary[];
  /** フォルダ（入れ子可） */
  folders: NoteFolderSummary[];
  /** 自分の下書き（新規作成の変更提案）。ツリーの「下書き」行になる */
  my_drafts: NoteDraftSummary[];
  settings?: NoteSectionSettings;
}

export interface NotesTreeResponse {
  status: string;
  data: {
    sections: NoteSection[];
    /** [[wikilink]] 解決用の title → note_id マップ（閲覧可能なノートのみ） */
    wikilink_map: Record<string, string>;
    /** セクションの作成・権限設定変更ができるか（master_admin のみ true） */
    can_manage: boolean;
  };
}

/** 作成者/更新者の表示用サマリー */
export interface NoteUserBrief {
  id: number;
  display_name: string;
  avatar: string | null;
}

/** レビュー情報（編集権限者にのみ返る集計値。カード表示ノート用） */
export interface NoteCardReview {
  ok_count: number;
  helpful_count: number;
}

export interface NoteDetail extends NoteAccessFields {
  id: string;
  section_id: string;
  /** 所属フォルダ（null = セクション直下） */
  folder_id: string | null;
  title: string;
  content: string;
  /** 表示形式（note = ノート / card = カード（共有TIPS）） */
  display_style: NoteDisplayStyle;
  /** カード表示時のカテゴリ */
  category: string;
  /** 有効期限（null = 無期限）。期限切れはやることリストに出ない */
  expires_at: string | null;
  /** 必須表示（やることリストの先頭に固定） */
  is_required: boolean;
  /** ホームのやることリストに投稿するか（共有ノート内の閲覧には影響しない） */
  is_published: boolean;
  sort_order: number;
  created: string | null;
  modified: string | null;
  created_by: NoteUserBrief | null;
  updated_by: NoteUserBrief | null;
  /** メンテナー以上にのみ含まれる（カード表示の既読/評価集計） */
  review?: NoteCardReview;
}

export interface NoteDetailResponse {
  status: string;
  data: { note: NoteDetail };
}

export interface NoteSectionResponse {
  status: string;
  data: { section: NoteSection };
}

// ── やることリスト（カード表示ノート） ───────────────────────────────

/** やることリスト（Home）に混在表示するカード（display_style=card のノート） */
export interface TodoNoteCard {
  id: string;
  title: string;
  content: string;
  category: string;
  expires_at: string | null;
  is_required: boolean;
  created: string | null;
  modified: string | null;
  created_by: NoteUserBrief | null;
  /** 自分の「役に立った」状態 */
  helpful: boolean;
}

export interface TodoNoteCardsResponse {
  status: string;
  data: { cards: TodoNoteCard[] };
}

export interface NoteReactionResponse {
  status: string;
  data: {
    reaction: { note_id: string; ok_at: string | null; helpful: boolean };
  };
}

// ── リクエストパラメータ ──────────────────────────────────────────────

export interface CreateNoteParams {
  section_id: string;
  title: string;
  content?: string;
  /** 追加先フォルダ（省略 = セクション直下） */
  folder_id?: string;
  /** 表示形式（省略 = note）。card でカード（共有TIPS）として作成 */
  display_style?: NoteDisplayStyle;
  category?: string;
  expires_at?: string | null;
  is_required?: boolean;
  is_published?: boolean;
}

export interface UpdateNoteParams {
  note_id: string;
  title?: string;
  content?: string;
  /** 表示形式の切替（ノート ⇄ カード） */
  display_style?: NoteDisplayStyle;
  category?: string;
  expires_at?: string | null;
  is_required?: boolean;
  is_published?: boolean;
  sort_order?: number;
  /** セクション移動（移動先の編集権限も必要） */
  section_id?: string;
  /**
   * 「更新して再送」: 全ユーザーの既読（OK）・評価（役に立った）を
   * リセットし、改めてやることリストに表示させる（カード表示ノート用）
   */
  reset_reactions?: boolean;
}

/** ロール設定の送信形（position / user_id はどちらか一方必須） */
export interface SectionRoleInput {
  role: SectionRoleName;
  position?: NotePosition;
  user_id?: number;
}

export interface SectionSettingsParams {
  title?: string;
  sort_order?: number;
  /** 非ログインユーザーにも閲覧を許可するか（外部公開） */
  is_public?: boolean;
  /** 対象プロジェクト（is_public でない限り 1 件以上必須） */
  project_ids?: string[];
  allowed_positions?: NotePosition[];
  allow_completed?: boolean;
  /** ロール設定（全置換） */
  roles?: SectionRoleInput[];
  require_owner_approval?: boolean;
  required_approvals?: number;
  /** ヘルプセンター割当（外部公開セクションのみ。各ロール全体で 1 つ） */
  help_center_client?: boolean;
  help_center_creator?: boolean;
}

/** GET /api/notes/help-center のレスポンス */
export interface HelpCenterResponse {
  status: string;
  data: {
    /** 自分のロールに割り当てられた外部公開セクション（未割当は null） */
    section_id: string | null;
    /** そのセクションの先頭ノート（ノートが無ければ null） */
    first_note_id: string | null;
  };
}

export interface UpdateSectionParams extends SectionSettingsParams {
  section_id: string;
}

// ── 変更提案（プルリクエスト） ────────────────────────────────────────

export type NoteChangeStatus = "draft" | "open" | "merged" | "rejected";
export type NoteChangeKind = "create" | "update";
export type NoteChangeTargetType = "note" | "card";
export type NoteChangeReviewAction =
  | "approve"
  | "request_changes"
  | "reject"
  | "request_owner_review";

/**
 * 提案内容（置換型）。ノートは title / content のみ、
 * カードは追加フィールドを持つ。
 * NOTE: BE の payload には thumbnail_key も残っているが、サムネイルは
 * 本文の先頭メディアから自動決定するため FE では扱わない。
 */
export interface NoteChangePayload {
  title: string;
  content: string;
  category?: string;
  expires_at?: string | null;
  is_required?: boolean;
  is_published?: boolean;
  /**
   * 新規作成の提案のみ: マージ時にこのフォルダ直下へ作成する。
   * PATCH でキー未指定なら既存の指定を維持、null でクリア（直下）。
   */
  folder_id?: string | null;
  /**
   * 新規作成の提案のみ: 配置コンテナ内の並び位置（null / 未指定 = 末尾）。
   * PATCH でキー未指定なら既存の指定を維持する
   */
  sort_order?: number | null;
  /**
   * 更新提案のみ: 移動の提案（マージ時にこのフォルダへ移す）。承認不要
   * フォルダの外へ出す移動は直接できないため、この形でレビューを通す
   */
  move?: NoteChangeMove;
}

/** 移動の提案の移動先（folder_id: null = セクション直下、sort_order: null = 末尾） */
export interface NoteChangeMove {
  folder_id: string | null;
  sort_order?: number | null;
}

/** 新規作成の提案の配置だけを変える（内容ではないため承認を stale にしない） */
export interface NoteChangePlacement {
  folder_id?: string | null;
  sort_order?: number | null;
}

export interface NoteChangeSummary {
  id: string;
  section_id: string;
  target_type: NoteChangeTargetType;
  /** 更新対象（新規作成の提案はマージまで null） */
  target_id: string | null;
  kind: NoteChangeKind;
  status: NoteChangeStatus;
  /** payload のタイトル（一覧表示用） */
  title: string;
  description: string;
  author: NoteUserBrief | null;
  created: string | null;
  modified: string | null;
  opened_at: string | null;
}

export interface NoteChangeReviewEntry {
  id: number;
  reviewer: NoteUserBrief | null;
  action: NoteChangeReviewAction;
  comment: string;
  created: string | null;
}

/** 保護ルールに基づく承認状態のサマリー */
export interface NoteChangeApproval {
  approvals: number;
  required_approvals: number;
  approved_by: NoteUserBrief[];
  owner_approval_required: boolean;
  owner_approved: boolean;
  mergeable: boolean;
}

export interface NoteChangeDetail extends NoteChangeSummary {
  payload: NoteChangePayload;
  /** 作成時点の対象スナップショット（新規作成の提案は null） */
  base: NoteChangePayload | null;
  /** 対象の現在値（対象が無ければ null） */
  current: NoteChangePayload | null;
  /** base と現在値が異なる = マージにコンフリクト解消が必要 */
  has_conflict: boolean;
  reviews: NoteChangeReviewEntry[];
  approval: NoteChangeApproval;
  merged_by: NoteUserBrief | null;
  merged_at: string | null;
  permissions: {
    can_update: boolean;
    can_review: boolean;
    can_merge: boolean;
  };
}

export interface NoteChangesResponse {
  status: string;
  data: { changes: NoteChangeSummary[] };
}

export interface NoteChangeResponse {
  status: string;
  data: {
    change: NoteChangeDetail;
    /** publish で公開したときに作成されたノート/カード */
    target?: NoteDetail;
  };
}

export interface MergeNoteChangeResponse {
  status: string;
  data: {
    change: NoteChangeDetail;
    target: NoteDetail;
  };
}

/** merge が 409 を返したときの error.data 形 */
export interface NoteChangeConflictData {
  status: string;
  code: "conflict";
  message: string;
  data: {
    base: NoteChangePayload | null;
    current: NoteChangePayload | null;
    proposed: NoteChangePayload;
  };
}

export interface GetNoteChangesParams {
  section_id?: string;
  status?: NoteChangeStatus;
  target_type?: NoteChangeTargetType;
  target_id?: string;
}

export interface CreateNoteChangeParams {
  target_type: NoteChangeTargetType;
  /** 更新対象（省略時は新規作成の提案。section_id が必要） */
  target_id?: string;
  section_id?: string;
  /** 提案内容。移動の提案（move）では省略でき、現在の内容のまま移動だけを提案する */
  payload?: NoteChangePayload;
  /** 更新提案のみ: 移動の提案 */
  move?: NoteChangeMove;
  description?: string;
  /** true でそのままプルリク送信（open） */
  submit?: boolean;
}

export interface UpdateNoteChangeParams {
  change_id: string;
  payload?: NoteChangePayload;
  /** 新規作成の提案の配置だけを変える（ツリー上の下書きの移動） */
  placement?: NoteChangePlacement;
  /**
   * true で、承認不要フォルダに配置された新規作成の提案をレビューなしで
   * そのまま公開する（マージ済みになり、レスポンスに data.target が付く）
   */
  publish?: boolean;
  description?: string;
  /** true で open（プルリク送信） */
  submit?: boolean;
}

export interface ReviewNoteChangeParams {
  change_id: string;
  action: NoteChangeReviewAction;
  comment?: string;
}

export interface MergeNoteChangeParams {
  change_id: string;
  /** コンフリクト解消済み（または最終調整した）内容 */
  resolved_payload?: NoteChangePayload;
  /** カード: 既読/評価をリセットしてやることリストへ再表示 */
  reset_reactions?: boolean;
}

export interface RoleCandidate extends NoteUserBrief {
  positions: NotePosition[];
}

export interface RoleCandidatesResponse {
  status: string;
  data: { users: RoleCandidate[] };
}

export interface NoteFolderResponse {
  status: string;
  data: { folder: NoteFolderSummary };
}

/** 並べ替え/フォルダ移動の 1 アイテム（カードも type=note として扱う） */
export interface ReorderItem {
  type: "folder" | "note";
  id: string;
  /** note のみ。null = セクション直下 */
  folder_id?: string | null;
  /** folder のみ。省略 = 変更なし / null = セクション直下（入れ子の移動） */
  parent_id?: string | null;
  sort_order: number;
}

export interface ReorderNotesParams {
  section_id: string;
  items: ReorderItem[];
}

export interface NoteReactionParams {
  note_id: string;
  /** true = 既読（OK）。やることリストから消える */
  ok?: boolean;
  /** 「役に立った」のトグル状態 */
  helpful?: boolean;
  /** 楽観更新用: やることリストのクエリ引数（Home からの呼び出し時に指定） */
  project_id?: string;
}

export interface GetNotesTreeParams {
  /**
   * 指定すると、セクション設定でそのプロジェクトを対象にした
   * セクションのみ返す。プロジェクト内のノート導線（下部バーの
   * アイコン表示判定・notes タブの絞り込み表示）用。
   */
  project_id?: string;
}

/** title を NFC 正規化して表記ゆれ（NFD/NFC）を吸収する（wikilink 解決キーのため重要） */
const normalizeTitle = <T extends { title?: string }>(body: T): T =>
  body.title === undefined
    ? body
    : ({ ...body, title: normalizeNfc(body.title) } as T);

export const notesApi = createApi({
  reducerPath: "notesApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["NotesTree", "Note", "NoteTodo", "NoteChange"],
  endpoints: (builder) => ({
    getNotesTree: builder.query<NotesTreeResponse, GetNotesTreeParams | void>({
      query: (params) =>
        params?.project_id
          ? `/api/notes?project=${encodeURIComponent(params.project_id)}`
          : "/api/notes",
      // 全件/プロジェクト絞り込みのどちらのキャッシュも同一タグにし、
      // ノート/セクション更新時の invalidate が全バリアントへ波及するようにする。
      providesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    getNote: builder.query<NoteDetailResponse, { note_id: string }>({
      query: ({ note_id }) => `/api/notes/${note_id}`,
      providesTags: (_result, _error, arg) => [
        { type: "Note", id: arg.note_id },
      ],
    }),

    createNote: builder.mutation<NoteDetailResponse, CreateNoteParams>({
      query: (body) => ({
        url: "/api/notes",
        method: "POST",
        body: normalizeTitle(body),
      }),
      // カード表示（公開中）で作成されるとやることリストにも出る
      invalidatesTags: [
        { type: "NotesTree", id: "TREE" },
        { type: "NoteTodo", id: "LIST" },
      ],
    }),

    updateNote: builder.mutation<NoteDetailResponse, UpdateNoteParams>({
      query: ({ note_id, ...body }) => ({
        url: `/api/notes/${note_id}`,
        method: "PATCH",
        body: normalizeTitle(body),
      }),
      // チェックボックスのトグル等が即時反映されるよう、詳細キャッシュを
      // 楽観的に更新する（失敗時は undo し、invalidatesTags で再取得される）。
      async onQueryStarted(
        { note_id, title, content },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          notesApi.util.updateQueryData("getNote", { note_id }, (draft) => {
            if (title !== undefined) draft.data.note.title = title;
            if (content !== undefined) draft.data.note.content = content;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "NotesTree", id: "TREE" },
        { type: "Note", id: arg.note_id },
        { type: "NoteTodo", id: "LIST" },
      ],
    }),

    deleteNote: builder.mutation<
      { status: string; message: string },
      { note_id: string }
    >({
      query: ({ note_id }) => ({
        url: `/api/notes/${note_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "NotesTree", id: "TREE" },
        { type: "Note", id: arg.note_id },
        { type: "NoteTodo", id: "LIST" },
      ],
    }),

    createSection: builder.mutation<
      NoteSectionResponse,
      SectionSettingsParams & { title: string }
    >({
      query: (body) => ({
        url: "/api/notes/sections",
        method: "POST",
        body: normalizeTitle(body),
      }),
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    updateSection: builder.mutation<NoteSectionResponse, UpdateSectionParams>({
      query: ({ section_id, ...body }) => ({
        url: `/api/notes/sections/${section_id}`,
        method: "PATCH",
        body: normalizeTitle(body),
      }),
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    deleteSection: builder.mutation<
      { status: string; message: string },
      { section_id: string }
    >({
      query: ({ section_id }) => ({
        url: `/api/notes/sections/${section_id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    // マイページ「ヘルプセンター」の遷移先解決（ロール別の割当セクション）。
    getHelpCenter: builder.query<HelpCenterResponse, void>({
      query: () => "/api/notes/help-center",
    }),

    createNoteFolder: builder.mutation<
      NoteFolderResponse,
      { section_id: string; title: string; parent_id?: string }
    >({
      query: (body) => ({
        url: "/api/notes/folders",
        method: "POST",
        body: normalizeTitle(body),
      }),
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    updateNoteFolder: builder.mutation<
      NoteFolderResponse,
      {
        folder_id: string;
        title?: string;
        sort_order?: number;
        /** 移動先の親フォルダ（null = セクション直下）。循環は 400 */
        parent_id?: string | null;
        /**
         * プルリク承認不要フォルダの設定（子孫にも効く）。オーナー承認必須の
         * セクションではオーナーのみ変更できる（それ以外は 403）
         */
        skip_review?: boolean;
      }
    >({
      query: ({ folder_id, ...body }) => ({
        url: `/api/notes/folders/${folder_id}`,
        method: "PATCH",
        body: normalizeTitle(body),
      }),
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    deleteNoteFolder: builder.mutation<
      { status: string; message: string },
      { folder_id: string }
    >({
      query: ({ folder_id }) => ({
        url: `/api/notes/folders/${folder_id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    /** フォルダの現在の共有リンク（無ければ data: null）。共有モーダルを開くたびに取る */
    getNoteShareLink: builder.query<
      { status: string; data: NoteShareLink | null },
      { folder_id: string }
    >({
      query: ({ folder_id }) =>
        `/api/notes/share_links?folder_id=${encodeURIComponent(folder_id)}`,
      keepUnusedDataFor: 0,
    }),

    /** 共有リンクの発行 / 設定変更（同じ URL のまま反映される） */
    saveNoteShareLink: builder.mutation<
      { status: string; data: NoteShareLink },
      SaveNoteShareLinkParams
    >({
      query: (body) => ({
        url: "/api/notes/share_links",
        method: "POST",
        body,
      }),
      // 共有中バッジ（folders[].is_shared）を更新する
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    deleteNoteShareLink: builder.mutation<
      { status: string; data: null },
      { folder_id: string }
    >({
      query: ({ folder_id }) => ({
        url: `/api/notes/share_links?folder_id=${encodeURIComponent(folder_id)}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    /** フォルダ内（子孫フォルダ含む）の全ノートを削除する（フォルダ自体は残す） */
    deleteNoteFolderFiles: builder.mutation<
      {
        status: string;
        message: string;
        data: { deleted_notes: number };
      },
      { folder_id: string }
    >({
      query: ({ folder_id }) => ({
        url: `/api/notes/folders/${folder_id}/files`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    /** セクション内の並べ替え / フォルダ移動の一括保存（D&D 用） */
    reorderNotes: builder.mutation<{ status: string }, ReorderNotesParams>({
      query: (body) => ({
        url: "/api/notes/reorder",
        method: "POST",
        body,
      }),
      // D&D 直後のツリー再取得で並びが確定する。楽観更新はツリーの
      // キャッシュ構造（全バリアント共有タグ）が複雑なため行わず、
      // FE 側はドラッグ中のローカル並び替え表示で体感を担保する。
      invalidatesTags: [{ type: "NotesTree", id: "TREE" }],
    }),

    /** やることリスト（Home）用: 公開中 × 期限内 × 未 OK のカード表示ノート一覧 */
    getTodoNoteCards: builder.query<
      TodoNoteCardsResponse,
      { project_id: string }
    >({
      query: ({ project_id }) =>
        `/api/notes/todo?project=${encodeURIComponent(project_id)}`,
      providesTags: [{ type: "NoteTodo", id: "LIST" }],
    }),

    // ── 変更提案（プルリクエスト） ────────────────────────────────

    /** 自分に見える変更提案の一覧（セクション / 状態 / 対象で絞り込み） */
    getNoteChanges: builder.query<NoteChangesResponse, GetNoteChangesParams>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params.section_id) search.set("section", params.section_id);
        if (params.status) search.set("status", params.status);
        if (params.target_type) search.set("target_type", params.target_type);
        if (params.target_id) search.set("target_id", params.target_id);
        const qs = search.toString();
        return `/api/notes/changes${qs ? `?${qs}` : ""}`;
      },
      providesTags: [{ type: "NoteChange", id: "LIST" }],
    }),

    getNoteChange: builder.query<NoteChangeResponse, { change_id: string }>({
      query: ({ change_id }) => `/api/notes/changes/${change_id}`,
      providesTags: (_result, _error, arg) => [
        { type: "NoteChange", id: arg.change_id },
      ],
    }),

    /** 下書き作成（submit=true でそのままプルリク送信） */
    createNoteChange: builder.mutation<
      NoteChangeResponse,
      CreateNoteChangeParams
    >({
      query: (body) => ({
        url: "/api/notes/changes",
        method: "POST",
        body: {
          ...body,
          ...(body.payload ? { payload: normalizeTitle(body.payload) } : {}),
        },
      }),
      invalidatesTags: [
        { type: "NoteChange", id: "LIST" },
        { type: "NotesTree", id: "TREE" },
      ],
    }),

    /** 本人による内容更新 / プルリク送信（draft → open） */
    updateNoteChange: builder.mutation<
      NoteChangeResponse,
      UpdateNoteChangeParams
    >({
      query: ({ change_id, ...body }) => ({
        url: `/api/notes/changes/${change_id}`,
        method: "PATCH",
        body: {
          ...body,
          ...(body.payload ? { payload: normalizeTitle(body.payload) } : {}),
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "NoteChange", id: "LIST" },
        { type: "NoteChange", id: arg.change_id },
        { type: "NotesTree", id: "TREE" },
      ],
    }),

    /** 本人による取り下げ（削除） */
    deleteNoteChange: builder.mutation<
      { status: string; message: string },
      { change_id: string }
    >({
      query: ({ change_id }) => ({
        url: `/api/notes/changes/${change_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "NoteChange", id: "LIST" },
        { type: "NoteChange", id: arg.change_id },
        { type: "NotesTree", id: "TREE" },
      ],
    }),

    /** レビュー（承認 / 差し戻し / 却下 / オーナーレビュー依頼） */
    reviewNoteChange: builder.mutation<
      NoteChangeResponse,
      ReviewNoteChangeParams
    >({
      query: ({ change_id, ...body }) => ({
        url: `/api/notes/changes/${change_id}/reviews`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "NoteChange", id: "LIST" },
        { type: "NoteChange", id: arg.change_id },
        { type: "NotesTree", id: "TREE" },
      ],
    }),

    /** 承認してマージ（コンフリクト時は 409。resolved_payload で再実行） */
    mergeNoteChange: builder.mutation<
      MergeNoteChangeResponse,
      MergeNoteChangeParams
    >({
      query: ({ change_id, ...body }) => ({
        url: `/api/notes/changes/${change_id}/merge`,
        method: "POST",
        body: {
          ...body,
          ...(body.resolved_payload
            ? { resolved_payload: normalizeTitle(body.resolved_payload) }
            : {}),
        },
      }),
      // マージは対象本体（ノート/カード表示ノート）へ反映されるため広めに invalidate
      invalidatesTags: (result, _error, arg) => [
        { type: "NoteChange", id: "LIST" },
        { type: "NoteChange", id: arg.change_id },
        { type: "NotesTree", id: "TREE" },
        { type: "NoteTodo", id: "LIST" },
        ...(result?.data?.change?.target_id
          ? [{ type: "Note" as const, id: result.data.change.target_id }]
          : []),
      ],
    }),

    /** ロール割り当て候補ユーザーの検索（オーナー / master_admin） */
    getRoleCandidates: builder.query<
      RoleCandidatesResponse,
      { section_id: string; q: string }
    >({
      query: ({ section_id, q }) =>
        `/api/notes/sections/${section_id}/role-candidates?q=${encodeURIComponent(q)}`,
    }),

    /** 既読 OK / 役に立った の保存（自分のリアクション。カード表示ノート用） */
    reactToNote: builder.mutation<NoteReactionResponse, NoteReactionParams>({
      query: ({ note_id, ok, helpful }) => ({
        url: `/api/notes/${note_id}/reaction`,
        method: "POST",
        body: {
          ...(ok !== undefined ? { ok } : {}),
          ...(helpful !== undefined ? { helpful } : {}),
        },
      }),
      // やることリストのカードを楽観更新する（OK は即時に消え、
      // 役に立ったはトグルが即時反映される。失敗時は undo）。
      async onQueryStarted(
        { note_id, ok, helpful, project_id },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = project_id
          ? dispatch(
              notesApi.util.updateQueryData(
                "getTodoNoteCards",
                { project_id },
                (draft) => {
                  if (ok === true) {
                    draft.data.cards = draft.data.cards.filter(
                      (c) => c.id !== note_id,
                    );
                  } else if (helpful !== undefined) {
                    const card = draft.data.cards.find(
                      (c) => c.id === note_id,
                    );
                    if (card) card.helpful = helpful;
                  }
                },
              ),
            )
          : null;
        try {
          await queryFulfilled;
        } catch {
          patchResult?.undo();
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "Note", id: arg.note_id },
      ],
    }),
  }),
});

export const {
  useGetNotesTreeQuery,
  useGetNoteQuery,
  useLazyGetNoteQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useLazyGetHelpCenterQuery,
  useGetTodoNoteCardsQuery,
  useReactToNoteMutation,
  useCreateNoteFolderMutation,
  useUpdateNoteFolderMutation,
  useDeleteNoteFolderMutation,
  useDeleteNoteFolderFilesMutation,
  useLazyGetNoteShareLinkQuery,
  useSaveNoteShareLinkMutation,
  useDeleteNoteShareLinkMutation,
  useReorderNotesMutation,
  useGetNoteChangesQuery,
  useGetNoteChangeQuery,
  useCreateNoteChangeMutation,
  useUpdateNoteChangeMutation,
  useDeleteNoteChangeMutation,
  useReviewNoteChangeMutation,
  useMergeNoteChangeMutation,
  useGetRoleCandidatesQuery,
} = notesApi;
